import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/auth")({
  ssr: false,
  beforeLoad: async () => {
    const { data } = await supabase.auth.getUser();
    if (data.user) throw redirect({ to: "/admin/settings" });
  },
  component: AuthPage,
  head: () => ({
    meta: [
      { title: "Admin Sign-in · Morningstar" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

function AuthPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN") {
        navigate({ to: "/admin/settings", replace: true });
      }
    });
    return () => sub.subscription.unsubscribe();
  }, [navigate]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      if (mode === "signin") {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      } else {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: `${window.location.origin}/admin/settings` },
        });
        if (error) throw error;
        setInfo("Account created. If email confirmation is required, check your inbox.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Authentication failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="min-h-screen bg-background flex items-center justify-center px-5 py-16">
      <div className="w-full max-w-md border border-emerald-deep/15 bg-card p-8 shadow-soft">
        <p className="eyebrow text-gold-deep mb-3">Morningstar · Admin</p>
        <h1 className="font-display text-3xl text-emerald-deep">
          {mode === "signin" ? "Sign in" : "Create account"}
        </h1>
        <p className="mt-2 text-sm text-foreground/70">
          Internal admin area. Sign in to manage WhatsApp greeting and site settings.
        </p>
        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <label className="block">
            <span className="text-[11px] uppercase tracking-[0.22em] text-emerald-deep font-semibold">Email</span>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 w-full border border-emerald-deep/20 bg-white px-3 py-2.5 text-sm text-emerald-deep focus:outline-none focus:border-gold"
            />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-[0.22em] text-emerald-deep font-semibold">Password</span>
            <input
              type="password"
              required
              minLength={6}
              autoComplete={mode === "signin" ? "current-password" : "new-password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 w-full border border-emerald-deep/20 bg-white px-3 py-2.5 text-sm text-emerald-deep focus:outline-none focus:border-gold"
            />
          </label>
          {error && <p className="text-sm text-red-700">{error}</p>}
          {info && <p className="text-sm text-emerald-deep/80">{info}</p>}
          <button
            type="submit"
            disabled={busy}
            className="w-full inline-flex items-center justify-center bg-gold px-7 py-3.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-deep hover:bg-[oklch(0.85_0.14_85)] transition-colors disabled:opacity-60"
          >
            {busy ? "Please wait…" : mode === "signin" ? "Sign in" : "Create account"}
          </button>
        </form>
        <button
          type="button"
          onClick={() => {
            setMode(mode === "signin" ? "signup" : "signin");
            setError(null);
            setInfo(null);
          }}
          className="mt-6 text-[11px] uppercase tracking-[0.22em] text-gold-deep font-semibold hover:text-emerald-deep transition-colors"
        >
          {mode === "signin" ? "Need an account? Create one →" : "Have an account? Sign in →"}
        </button>
      </div>
    </main>
  );
}